/**
 * bn-verfuegbarkeit.js — Web Component für theme/blocks/bn-verfuegbarkeit.liquid
 *
 * Horizon rendert das Verfügbarkeits-Badge bei einem Variantenwechsel nicht
 * neu. Die Komponente hört deshalb auf das Standard-Event
 * shopify:product:select (Vorbild: assets/product-inventory.js im
 * Stock-Theme) und morpht ihren Inhalt aus dem frisch servergerenderten
 * HTML des Events. Der Zustand kommt damit immer vom Server — im Client
 * liegen keine Bestandsdaten, erst recht keine exakten Mengen.
 *
 * connectedCallback ist idempotent (AbortController), damit die Komponente
 * Morphs und Editor-Reorder ohne doppelte Listener übersteht.
 *
 * Gemorpht wird nur [data-bn="inhalt"]. Die Live-Region (leeres p mit
 * role="status") liegt bewusst daneben: Screenreader beobachten das Element,
 * nicht seinen Inhalt — eine getauschte Region ist eine neue Region und wird
 * nicht angesagt. Sie startet leer und bekommt erst nach einem Wechsel Text,
 * auch im Fall ohne Badge (ausverkaufte Variante).
 */
import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

class BnVerfuegbarkeit extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {string} Zuletzt angesagter Zustand — verhindert Ansagen ohne Änderung. */
  #letzterZustand = '';

  connectedCallback() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.#letzterZustand = this.#zustandstext();

    this.closest('.shopify-section, dialog')?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal: this.#listeners.signal }
    );
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
  }

  /**
   * Der Text, den ein Screenreader zum aktuellen Zustand hören soll: der
   * sichtbare Badge-Text, sonst der gepflegte Ersatztext. Ist auch der leer,
   * wird nichts angesagt.
   * @returns {string}
   */
  #zustandstext() {
    const badge = this.querySelector('[data-bn="inhalt"] .bn-vf__badge');
    if (badge) return (badge.textContent ?? '').replace(/\s+/g, ' ').trim();
    return (this.dataset.textOhneBadge ?? '').trim();
  }

  /**
   * Neuen Zustand in die Live-Region schreiben — aber nur, wenn er sich
   * geändert hat. Sonst würde jeder Variantenwechsel innerhalb desselben
   * Zustands dieselbe Ansage wiederholen.
   */
  #ansagen() {
    const region = this.querySelector('[data-bn="status"]');
    if (!region) return;

    const text = this.#zustandstext();
    if (text === this.#letzterZustand) return;

    this.#letzterZustand = text;
    region.textContent = text;
  }

  /**
   * Variantenwechsel (und Produktwechsel bei Combined Listings): den eigenen
   * servergerenderten Gegenpart im frisch geladenen Dokument suchen und die
   * Kinder des Inhaltscontainers morphen. Ohne HTML im Event bleibt der letzte
   * Serverstand stehen — lieber ein alter ehrlicher Zustand als ein
   * clientseitig geratener.
   * @param {Event & { promise?: Promise<{ detail?: any }> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;

        const blockId = this.dataset.blockId;
        const counterpart =
          (blockId
            ? html.querySelector(`bn-verfuegbarkeit[data-block-id="${CSS.escape(blockId)}"]`)
            : null) ?? (detail.newProduct ? html.querySelector('bn-verfuegbarkeit') : null);

        if (!(counterpart instanceof HTMLElement)) return;

        const eigenerInhalt = this.querySelector('[data-bn="inhalt"]');
        const neuerInhalt = counterpart.querySelector('[data-bn="inhalt"]');
        if (!(eigenerInhalt instanceof HTMLElement) || !(neuerInhalt instanceof HTMLElement)) return;

        morph(eigenerInhalt, neuerInhalt, { childrenOnly: true });
        this.#ansagen();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[bn-verfuegbarkeit] Variantenwechsel nicht verarbeitet:', error);
        }
      });
  };
}

if (!customElements.get('bn-verfuegbarkeit')) {
  customElements.define('bn-verfuegbarkeit', BnVerfuegbarkeit);
}
